import { Box, Typography } from "@mui/material";
import logo from "../../../assets/logo-passtrack.png";
import { STEP_META } from "../types";
import { passtrackColors } from "../theme";

interface WizardSidebarProps {
  step: number;
  onGoToStep: (step: number) => void;
  completedSteps?: Record<number, boolean>;
  horizontal?: boolean;
}

export default function WizardSidebar({
  step,
  onGoToStep,
  completedSteps = {},
  horizontal = false,
}: WizardSidebarProps) {
  const nav = (
    <Box
      sx={{
        display: "flex",
        flexDirection: horizontal ? "row" : "column",
        gap: horizontal ? 1 : 0.25,
        overflowX: horizontal ? "auto" : "visible",
      }}
    >
      {STEP_META.map((s, i) => {
        const n = i + 1;
        const current = n === step;
        const stepDone = Boolean(completedSteps[n]) && !current;
        return (
          <Box
            key={s.title}
            component="button"
            onClick={() => onGoToStep(n)}
            sx={{
              display: "grid",
              gridTemplateColumns: "28px 1fr",
              gap: 1.5,
              alignItems: "start",
              textAlign: "left",
              border: "1px solid transparent",
              background: current ? passtrackColors.cloud : "transparent",
              borderRadius: 1.5,
              p: 1.5,
              cursor: "pointer",
              minWidth: horizontal ? 220 : "auto",
              flex: horizontal ? "0 0 auto" : "initial",
              font: "inherit",
              "&:hover": { background: passtrackColors.mist },
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "inherit",
                fontSize: 13,
                fontWeight: 700,
                background: current
                  ? passtrackColors.blue
                  : stepDone
                    ? passtrackColors.greenMist
                    : "#fff",
                color: current
                  ? "#fff"
                  : stepDone
                    ? passtrackColors.green700
                    : passtrackColors.textMuted,
                border: `1px solid ${
                  current
                    ? passtrackColors.blue
                    : stepDone
                      ? passtrackColors.green200
                      : passtrackColors.borderDefault
                }`,
              }}
            >
              {stepDone ? "✓" : n}
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3, pt: 0.3 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: 14,
                  color: current ? passtrackColors.blue900 : passtrackColors.ink,
                }}
              >
                {s.title}
              </Typography>
              {!horizontal && (
                <Typography sx={{ fontSize: 12, color: passtrackColors.textMuted }}>
                  {s.hint}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );

  if (horizontal) {
    return (
      <Box
        sx={{
          background: "#fff",
          borderBottom: `1px solid ${passtrackColors.borderSubtle}`,
          p: 1.5,
        }}
      >
        {nav}
      </Box>
    );
  }

  return (
    <Box
      component="aside"
      sx={{
        width: 296,
        flex: "none",
        background: "#fff",
        borderRight: `1px solid ${passtrackColors.borderSubtle}`,
        display: "flex",
        flexDirection: "column",
        p: 3,
        gap: 4,
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        <img src={logo} alt="PassTrack" style={{ height: 26, alignSelf: "flex-start" }} />
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: passtrackColors.blue500,
          }}
        >
          Control de accesos
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 19, color: passtrackColors.ink }}>
          Configurar propiedad
        </Typography>
        <Typography sx={{ fontSize: 13, color: passtrackColors.textMuted }}>
          Cinco pasos para dejar la propiedad lista para registrar accesos.
        </Typography>
      </Box>

      <Box component="nav" sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
        {nav}
      </Box>

      <Box
        sx={{
          mt: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
          background: passtrackColors.greenMist,
          borderRadius: 1.5,
          p: 2,
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: 13, color: passtrackColors.green700 }}>
          Progreso del asistente
        </Typography>
        <Typography sx={{ fontSize: 12, lineHeight: 1.5, color: passtrackColors.green700 }}>
          Tu información se mantiene mientras avanzas entre los pasos. Los
          campos con * son obligatorios.
        </Typography>
      </Box>
    </Box>
  );
}
